import { cn } from "@paribus/shared-ui";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

// No hover border: --input and --border-strong resolve to the same value, so a hover class here would be inert.
const inputVariants = cva(
  "flex w-full rounded-md border bg-background text-foreground transition-colors duration-fast ease-standard placeholder:text-text-faint focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled read-only:bg-surface-subtle",
  {
    variants: {
      inputSize: {
        sm: "h-9 px-3 text-dense",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-4 text-sm",
      },
      invalid: {
        true: "border-destructive focus-visible:shadow-focus-danger",
        false: "border-input focus-visible:shadow-focus",
      },
    },
    defaultVariants: {
      inputSize: "md",
      invalid: false,
    },
  }
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    Omit<VariantProps<typeof inputVariants>, "inputSize"> {
  size?: "sm" | "md" | "lg";
  leadingIcon?: React.ReactNode;
  trailingSlot?: React.ReactNode;
}

// `inputVariants` uses `inputSize` internally because `size` collides with the native input attribute.
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, size = "md", invalid = false, leadingIcon, trailingSlot, ...props }, ref) => {
    const control = (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          inputVariants({ inputSize: size, invalid }),
          leadingIcon && "pl-10",
          trailingSlot && "pr-10",
          className
        )}
        {...props}
      />
    );

    if (!leadingIcon && !trailingSlot) return control;

    return (
      <div className="relative w-full">
        {leadingIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-faint [&_svg]:size-4">
            {leadingIcon}
          </span>
        ) : null}
        {control}
        {trailingSlot ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-faint [&_svg]:size-4">
            {trailingSlot}
          </span>
        ) : null}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input, inputVariants };
